import { PathRoute, useLocation } from "@pomle/react-router-paths";
import { Sticky, ViewStack } from "@pomle/react-viewstack";
import { Direction } from "@pomle/react-viewstack-transitions";
import { useEffect, useRef } from "react";
import { useTrackingContext } from "render/context/TrackingContext";
import { usePatientQuery } from "render/hooks/api/queries/usePatientQuery";
import { paths } from "render/routes/paths";
import { AssignmentView } from "render/views/AssignmentView";
import { CheckInView } from "render/views/CheckInView";
import { CheckoutFailView } from "render/views/CheckoutFailView";
import { CheckoutSuccessView } from "render/views/CheckoutSuccessView";
import { CheckoutView } from "render/views/CheckoutView";
import { ClaimBookingToken } from "render/views/ClaimBookingToken";
import { ContactView } from "render/views/ContactView";
import { HomeView } from "render/views/HomeView";
import { MemberInviteView } from "render/views/MemberInviteView";
import { OnboardingView } from "render/views/OnboardingView";
import { OnboardingView as OnboardingV2 } from "render/views/OnboardingViewV2";
import { ProfileView } from "render/views/ProfileView";
import { ReportView } from "render/views/Report";
import { AppointmentOnboarding } from "render/views/appointment/AppointmentOnboarding";
import { AppointmentView } from "render/views/appointment/AppointmentView";
import { ConciergeCheckInView } from "render/views/concierge/ConciergeCheckInView";
import { AtriumSignIn } from "render/views/partners/atrium/AtriumSignIn";
import { mount } from "./lib/mount";
import { BookingRoutes } from "./routes/BookingRoutes";

export function SessionRoutes() {
  const historyRef = useRef<string[]>([]);
  const location = useLocation();
  const { hasLoaded, gtm, conversionsTracking } = useTrackingContext();
  const patientQuery = usePatientQuery();

  useEffect(() => {
    if (hasLoaded || patientQuery.data == null) {
      return;
    }
    gtm.Init(patientQuery.data);
    gtm.GiveConsent();
    conversionsTracking.TrackLogin();
  }, [hasLoaded, patientQuery.data, gtm, conversionsTracking]);

  useEffect(() => {
    if (patientQuery.data == null) {
      return;
    }
    if (paths.feedback.match(location.pathname) !== 0) {
      return;
    }
    window.location.href = `https://start.nekohealth.com/feedback/index.html?i=${
      patientQuery.data?.patientId
    }&e=${
      patientQuery.data?.contactDetails.email
    }&p=${patientQuery.data?.contactDetails.phoneNumbers.at(0)}`;
  }, [patientQuery.data, location.pathname]);

  if (historyRef.current[historyRef.current.length - 1] !== location.pathname) {
    historyRef.current.push(location.pathname);
  }

  return (
    <ViewStack>
      <PathRoute path={paths.root}>
        {(match) => <Sticky>{match?.exact && <HomeView />}</Sticky>}
      </PathRoute>
      <PathRoute path={paths.profile}>
        {mount(ProfileView, { animation: "slide", direction: Direction.Up })}
      </PathRoute>
      <PathRoute path={paths.concierge.checkIn}>
        {mount(ConciergeCheckInView, {
          animation: "fade",
        })}
      </PathRoute>
      <PathRoute path={paths.assignment}>
        {mount(AssignmentView, {
          animation: "slide",
          direction: Direction.Right,
        })}
      </PathRoute>
      <PathRoute path={paths.atriumSignIn}>
        {mount(AtriumSignIn, { animation: "slide", direction: Direction.Up })}
      </PathRoute>

      <PathRoute path={paths.memberInviteCryptic}>
        {mount(MemberInviteView, { animation: "fade" })}
      </PathRoute>
      <PathRoute path={paths.memberInvite}>
        {mount(MemberInviteView, { animation: "fade" })}
      </PathRoute>
      <PathRoute path={paths.claimBookingToken}>
        {mount(ClaimBookingToken, {
          animation: "slide",
          direction: Direction.Up,
        })}
      </PathRoute>
      <PathRoute path={paths.checkIn}>
        {mount(CheckInView, {
          animation: "slide",
          direction: Direction.Up,
        })}
      </PathRoute>

      <PathRoute path={paths.checkout}>
        {mount(CheckoutView, {
          animation: "slide",
          direction: Direction.Up,
        })}
      </PathRoute>
      <PathRoute path={paths.checkoutSuccess}>
        {mount(CheckoutSuccessView, {
          animation: "slide",
          direction: Direction.Down,
        })}
      </PathRoute>
      <PathRoute path={paths.checkoutFail}>
        {mount(CheckoutFailView, {
          animation: "slide",
          direction: Direction.Down,
        })}
      </PathRoute>

      <BookingRoutes />

      <PathRoute path={paths.onboarding}>
        {mount(OnboardingView, {
          animation: "slide",
          direction: Direction.Down,
        })}
      </PathRoute>
      <PathRoute path={paths.onboardingV2}>
        {mount(OnboardingV2, {
          animation: "slide",
          direction: Direction.Down,
        })}
      </PathRoute>

      <PathRoute path={paths.report}>
        {mount(ReportView, {
          animation: "slide",
          direction: Direction.Down,
        })}
      </PathRoute>
      <PathRoute path={paths.appointmentSummary}>
        {mount(ReportView, {
          animation: "slide",
          direction: Direction.Down,
        })}
      </PathRoute>

      <PathRoute path={paths.checkInAppointment}>
        {mount(AppointmentView, {
          animation: "slide",
          direction: Direction.Right,
        })}
      </PathRoute>

      <PathRoute path={paths.appointment}>
        {mount(AppointmentView, {
          animation: "slide",
          direction: Direction.Right,
        })}
      </PathRoute>

      <PathRoute path={paths.appointmentOnboarding}>
        {mount(AppointmentOnboarding, {
          animation: "slide",
          direction: Direction.Right,
        })}
      </PathRoute>

      <PathRoute path={paths.contact}>
        {mount(ContactView, {
          animation: "slide",
          direction: Direction.Right,
        })}
      </PathRoute>
    </ViewStack>
  );
}
